import { Model, GQL } from "@binale-tech/shared";
import { CATEGORY_DIV, CATEGORY_EMPTY, Category } from "@domain/models/Category";
import { Creditor, CREDITOR_DIV, CREDITOR_EMPTY, Debitor, DEBITOR_DIV, DEBITOR_EMPTY } from "@domain/models/Creditor";
import type { Payment } from "@domain/models/Payment";
import { type Tag, TAG_DIV, TAG_EMPTY } from "@domain/models/Tag";
import { BuTaxesSKR } from "@domain/models/BuTaxUtils";
import {
    type AiReviewResult,
    type IBelegfeld,
    type IBrutto,
    type ICCD,
    type IExtraData,
    type IGenericItem,
    type IGenericRecord,
    type ITag,
    type ITax,
    type ItemRecordContext,
    type IText,
} from "@domain/models/Interfaces";
import { MoneyUtils } from "@domain/models/utils/MoneyUtils";
import { UnserializeItemMixin } from "@domain/models/mixins";
import { IstMNFUtils } from "@domain/models/utils/IstMNFUtils";
import { GenericRecordUtils } from "@domain/models/utils/GenericRecordUtils";

export class GenericRecord implements IGenericRecord {
    date?: Date;
    period?: number;
    day?: number;
    year?: number;
    num?: string; // Belegfeld1
    brutto?: number;
    currency?: Model.CurrencyConfig;
    originalAmount?: number; // only when currency is used
    documents?: GQL.IEntityDocumentId[];
    creditor?: Creditor;
    category?: Category;
    debetor?: Debitor;
    lastschrift?: boolean;
    falligkeit?: Date;
    review?: "changed" | "ok" | "question" | "error";
    aiReview?: AiReviewResult;
    recordOrigin?: GQL.IRecordOriginType;
    key?: string;
    items: IGenericItem[];
    avis?: boolean;
    color?: string;
    updatedAt?: Date;
    updatedBy?: string;
    priority?: number;
    contactId?: string;
    countryCode?: string;
    vatNumber?: string;
    notesColor?: GQL.INotesColor;
    readonly journaled?: boolean;
    readonly draft?: boolean;
    readonly cancellation?: "cancelled" | "counterweight";
    /** ELSTER exports this record was filed in; appended by the backend, never written here. */
    readonly ustvaExportIds?: string[];
    readonly saldoSourceId?: string;
    bankingTransactionId?: string;
    cashRegisterTransactionId?: string;

    constructor(v: IGenericRecord = {}) {
        v.key = v.key?.toString() ?? null;

        v.date = v.date instanceof Date ? v.date : new Date(v.date);

        v.falligkeit = this.parseDate(v.falligkeit);
        v.creditor = v.creditor ? Creditor.unserialize(v.creditor) : undefined;
        v.category = v.category ? Category.unserialize(v.category) : undefined;
        v.debetor = v.debetor ? Debitor.unserialize(v.debetor) : undefined;

        v.items = (v.items || []).map((i: IGenericItem) => new GenericItem(i));
        v.documents = v.documents ?? [];
        Object.assign(this, v);
        this.calculateBrutto();
    }

    getProductKey(): GQL.IProductKey {
        return null;
    }

    clone(): GenericRecord {
        return Object.assign(
            Object.create(Object.getPrototypeOf(this)),
            new GenericRecord(JSON.parse(JSON.stringify(this))),
        );
    }

    protected parseDate(v?: Date | string) {
        if (v instanceof Date) {
            return v;
        }
        if (!v) {
            return undefined;
        }
        return new Date(v);
    }

    savePreProcess(ext: number) {
        if (!Number.isFinite(this.period)) {
            this.period = this.date.getMonth() + 1;
        }
        const makePeriodAdjustment = () => {
            if (this.period === Model.Period.FirstDayOfYear) {
                this.date.setHours(3);
            } else if (this.period === Model.Period.LastDayOfYear) {
                this.date.setHours(21);
            } else {
                this.date.setHours(12);
            }
        };
        makePeriodAdjustment();
        this.day = this.date.getDate();
        this.year = this.date.getFullYear();
        this.category && this.category.fixNum(ext);
        this.creditor && this.creditor.fixNum(ext);
        this.debetor && this.debetor.fixNum(ext);
        this.items.forEach((item) => {
            item.category && item.category.fixNum(ext);
            item.creditor && item.creditor.fixNum(ext);
            item.debetor && item.debetor.fixNum(ext);
        });
    }

    getOpenBrutto(payments: Payment[] = []) {
        return GenericRecordUtils.getOpenBrutto(this.getBrutto(), payments);
    }

    calculateBrutto() {
        this.brutto = this.getBrutto();
        return this;
    }

    getRecordCategoryCreditor(): Model.IExtNum {
        if (this.creditor) {
            return this.creditor;
        }
        if (this.category) {
            return this.category;
        }
        if (this.debetor) {
            return this.debetor;
        }
        return CREDITOR_EMPTY;
    }

    getItemCategoryCreditor(): Model.IExtNum {
        const cred = this.getItemCreditor();
        if (cred === CREDITOR_DIV) {
            return cred;
        }
        const cat = this.getItemCategory();
        if (cat === CATEGORY_DIV) {
            return cat;
        }
        const deb = this.getItemDebitor();
        if (deb !== DEBITOR_EMPTY) {
            return deb;
        }
        if (cred !== CREDITOR_EMPTY) {
            return cred;
        }
        if (cat !== CATEGORY_EMPTY) {
            return cat;
        }
        if (deb === DEBITOR_EMPTY && cred === CREDITOR_EMPTY && cat === CATEGORY_EMPTY) {
            return CATEGORY_EMPTY;
        }
        // at this point the only case left = there is category at one item and creditor ad other item.
        // return any div
        return CATEGORY_DIV;
    }

    public static generateSingleItemRecord(
        record: GenericRecord,
        item: IGenericItem,
        preserveKey?: boolean,
    ): GenericRecord {
        const { key, ...recordData } = record;
        const data: IGenericRecord = {
            ...recordData,
            brutto: item.brutto,
            key: preserveKey ? record.key : null,
            items: [item],
        };
        return Object.assign(Object.create(Object.getPrototypeOf(record)), data);
    }

    // Mixin prototypes

    getTag(): Tag {
        const v = this.items[0].tag;
        const same = this.items.every((item: ITag) => {
            if (!v && !item.tag) {
                return true;
            }
            return item.tag?.equalsTo(v);
        });
        return same ? v || TAG_EMPTY : TAG_DIV;
    }

    getItemCategory(): Category {
        const { category } = this.items[0];
        if (!category) {
            return CATEGORY_EMPTY;
        }
        const same = this.items.every((item) => item.category?.equalsTo(category));
        return same ? category : CATEGORY_DIV;
    }

    protected getItemCreditor(): Creditor {
        try {
            const { creditor } = this.items[0];
            if (!creditor) {
                return CREDITOR_EMPTY;
            }
            const same = this.items.every((item) => item.creditor?.equalsTo(creditor));
            return same ? creditor : CREDITOR_DIV;
        } catch (e) {
            throw e;
        }
    }

    protected getItemDebitor(): Creditor {
        const { debetor: debitor } = this.items[0];
        if (!debitor) {
            return DEBITOR_EMPTY;
        }
        const same = this.items.every((item) => item.debetor?.equalsTo(debitor));
        return same ? debitor : DEBITOR_DIV;
    }

    getBrutto(): number {
        return (this.items || []).reduce((caret: number, current: IBrutto) => caret + current.brutto, 0);
    }

    getOriginalAmount(): number {
        return (this.items || []).reduce((caret: number, current: IBrutto) => caret + (current.originalAmount || 0), 0);
    }

    getText(): string {
        const v = this.items[0].text;
        const same = this.items.every((item: IText) => item.text === v);
        return same ? v : "Div.";
    }

    getText2(): string {
        const v = this.items[0].text2;
        const same = this.items.every((item: IText) => item.text2 === v);
        return same ? v : "Div.";
    }

    getUSt13b(): string {
        const v = this.items[0].USt13b;
        const same = this.items.every((item: ITax) => item.USt13b === v);
        return same ? (v || "").toString() : "Div.";
    }

    getBelegfeld1(): string {
        const v = this.items[0].belegfeld1;
        const same = this.items.every((item: IBelegfeld) => item.belegfeld1 === v);
        return same ? v : "Div.";
    }

    getBelegfeld2(): string {
        const v = this.items[0].belegfeld2;
        const same = this.items.every((item: IBelegfeld) => item.belegfeld2 === v);
        return same ? v : "Div.";
    }

    getVatEuro(skr: number): number {
        return this.items.reduce(
            (caret, current) =>
                caret +
                current.getVatEuro(
                    {
                        recordKonto: this.getRecordCategoryCreditor(),
                        product: this.getProductKey(),
                        year: this.year,
                        period: this.period,
                    },
                    skr,
                ),
            0,
        );
    }

    getNetto(skr: number): number {
        return this.items.reduce(
            (caret, current) =>
                caret +
                current.getNetto(
                    {
                        recordKonto: this.getRecordCategoryCreditor(),
                        product: this.getProductKey(),
                        year: this.year,
                        period: this.period,
                    },
                    skr,
                ),
            0,
        );
    }

    getTableBuText(skr: number): string {
        const bu = this.items[0].bu;
        const same = this.items.every((item: ITax & ICCD) => item.bu === bu);
        if (!same) {
            return "Div.";
        }
        return BuTaxesSKR.getBuTaxYearPeriod(
            bu,
            skr,
            this.year,
            this.period,
            BuTaxesSKR.dangerouslyGetBuTimeframesData(this.year).buTimeframes,
        ).text;
    }

    getTableBu(skr: number): string {
        const bu = this.items[0].getBu(this, skr);
        const same = this.items.every((item: GenericItem) => item.getBu(this, skr) === bu);
        return same ? bu : "Div.";
    }
}

export class GenericItem implements IGenericItem {
    constructor(i: IGenericItem = {}) {
        i.brutto = typeof i.brutto === "number" ? +i.brutto.toFixed(0) : 0;
        i = UnserializeItemMixin.preUnserialize(i);
        Object.assign(this, i);
    }

    bu: Model.Bu;
    category?: Category;
    creditor?: Creditor;
    debetor?: Debitor;
    brutto?: number;
    originalAmount?: number; // only when currency is used on the record level
    tag?: Tag;
    text?: string;
    text2?: string;
    belegfeld1?: string;
    belegfeld2?: string;
    extra?: IExtraData;
    USt13b?: Model.USt13bOption;

    getCategoryCreditor(): Model.IExtNum {
        if (this.creditor) {
            return this.creditor;
        }
        if (this.category) {
            return this.category;
        }
        if (this.debetor) {
            return this.debetor;
        }
        return CREDITOR_EMPTY;
    }

    // mixins
    getNetto(recordData: ItemRecordContext, skr: number) {
        return this.calculateNetto(this.brutto, recordData, skr);
    }

    getOriginalCurrencyNetto(recordData: ItemRecordContext, skr: number) {
        return this.calculateNetto(this.originalAmount || this.brutto, recordData, skr);
    }

    protected calculateNetto(brutto: number, recordData: ItemRecordContext, skr: number) {
        if (!brutto) {
            return 0;
        }
        const { recordKonto, product } = recordData;
        const { buTimeframes, taxation, kontoExt } = BuTaxesSKR.dangerouslyGetBuTimeframesData(recordData.year);
        const buTax = BuTaxesSKR.getBuTaxYearPeriod(this.bu, skr, recordData.year, recordData.period, buTimeframes);
        if (BuTaxesSKR.isTaxOnTop(buTax)) {
            return brutto;
        }
        const itemKonto = this.getCategoryCreditor();
        // IST FE through a non-Geldkonto releases no USt (PO, 2026-11-04): no tax, netto = brutto
        const { skipTaxLegs } = IstMNFUtils.getLogic(
            product,
            { taxation, kontoExt, skr },
            recordKonto,
            itemKonto,
            buTax.bu,
        );
        if (skipTaxLegs) {
            return brutto;
        }
        return MoneyUtils.getNettoFromBrutto(brutto, buTax.percent);
    }

    getVatEuro(recordData: ItemRecordContext, skr: number) {
        if (!this.brutto) {
            return 0;
        }
        return this.brutto - this.getNetto(recordData, skr);
    }

    getTax(percent: number, taxOnTop?: boolean) {
        if (!this.brutto) {
            return 0;
        }
        if (!percent) {
            return 0;
        }
        // MoneyUtils rounds half away from zero, so a counterweight's VAT leg mirrors the
        // original exactly (Math.round rounds ±x,5 toward +∞ → 1-cent Storno residual).
        if (taxOnTop) {
            return MoneyUtils.getTaxOnTop(this.brutto, percent);
        }
        return this.brutto - MoneyUtils.getNettoFromBrutto(this.brutto, percent);
    }

    getBu(record: GenericRecord, skr: number, isDatevExport?: boolean): string {
        const isCounterweight = record.cancellation === "counterweight";
        if (!this.bu) {
            return isCounterweight ? "20" : "";
        }
        const isAutoBu = this.category && this.category.isAutoBu();
        if (isAutoBu && isCounterweight) {
            return "20";
        }
        if (isDatevExport && isAutoBu) {
            return "";
        }
        if (isCounterweight) {
            const { buTimeframes } = BuTaxesSKR.dangerouslyGetBuTimeframesData(record.date.getFullYear());
            const buTax = BuTaxesSKR.getBuTaxYearPeriod(this.bu, skr, record.year, record.period, buTimeframes);
            return Number(buTax.GU).toString();
        }
        return Number(this.bu).toString();
    }
}

export class RecordER extends GenericRecord {
    getProductKey(): GQL.IProductKey {
        return GQL.IProductKey.Er;
    }
}

export class RecordERAnzahlung extends GenericRecord {
    getProductKey(): GQL.IProductKey {
        return GQL.IProductKey.ErA;
    }
}

export class RecordKB extends GenericRecord {
    getProductKey(): GQL.IProductKey {
        return GQL.IProductKey.Kb;
    }
}

export class RecordBank extends RecordKB {
    getProductKey(): GQL.IProductKey {
        return GQL.IProductKey.Bank;
    }
}

export class RecordFE extends GenericRecord {
    getProductKey(): GQL.IProductKey {
        return GQL.IProductKey.Fe;
    }
}

export class RecordPOS extends GenericRecord {
    getProductKey(): GQL.IProductKey {
        return GQL.IProductKey.Pos;
    }
}

export class RecordLA extends GenericRecord {
    getProductKey(): GQL.IProductKey {
        return GQL.IProductKey.La;
    }
}

export class RecordDeb extends GenericRecord {
    getProductKey(): GQL.IProductKey {
        return GQL.IProductKey.Deb;
    }
}
